/**
 * SettingsPage - 4 tabs (Profile, Security, Team, Developers).
 *
 * Phase 4 wires the Profile + Security tabs as stubs that show the
 * relevant data from the auth store. The Profile tab supports name +
 * password change (live calls to `/api/v1/me` and
 * `/api/v1/me/password` mirroring Auware) plus the Include Ticket
 * History email preference (2026-07-14, self-service per Greg). The Security tab shows TOTP
 * + passkey + sessions state read-only with TODO callouts; the live
 * setup wizard is finished in a follow-up phase.
 *
 * Team is hidden unless the user is a system_admin. Phase 12 fills it
 * in.
 *
 * Developers is system_admin-only and lives here per SPEC §5: API key
 * list + create + revoke. The full create flow surfaces the raw_key
 * via a show-once modal. Phase 9.10.
 *
 * Visual shape mirrors Auware's SettingsPage tab strip / card grouping.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";

import Button from "@/components/ui/Button";
import Card from "@/components/ui/Card";
import { Alert, Badge, Input, Modal, Select, Spinner } from "@/components/ui";
import { useAuthStore } from "@/stores/auth";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useProfile, useUpdateProfile } from "@/hooks/useProfile";
import {
  useAdminUsers,
  useCreateAdminUser,
  useUpdateAdminUser,
  extractDetailMessage,
} from "@/hooks/useAdminUsers";
import {
  useApiKeys,
  useCreateApiKey,
  useRevokeApiKey,
} from "@/hooks/useApiKeys";
import { ApiKeyTable } from "@/components/admin/ApiKeyTable";
import { CreateApiKeyModal } from "@/components/admin/CreateApiKeyModal";
import { RevealOnceModal } from "@/components/admin/RevealOnceModal";
import api from "@/lib/api";
import type {
  AdminUserResponse,
  ApiKeyCreateRequest,
  SystemRole,
} from "@/types/api";

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

const BASE_TABS = [
  { key: "profile", label: "Profile", icon: "fa-duotone fa-thin fa-user" },
  {
    key: "security",
    label: "Security",
    icon: "fa-duotone fa-thin fa-shield-halved",
  },
  { key: "team", label: "Team", icon: "fa-duotone fa-thin fa-users" },
  {
    key: "developers",
    label: "Developers",
    icon: "fa-duotone fa-thin fa-code",
  },
] as const;

type TabKey = (typeof BASE_TABS)[number]["key"];

// ---------------------------------------------------------------------------
// Profile tab
// ---------------------------------------------------------------------------

function ProfileTab(): JSX.Element {
  const user = useAuthStore((s) => s.user);

  const [name, setName] = useState(user?.name ?? "");
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSaving, setProfileSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSaving, setPasswordSaving] = useState(false);

  const handleProfileSave = async (e: FormEvent) => {
    e.preventDefault();
    setProfileError(null);
    setProfileSuccess(null);
    if (!name.trim()) {
      setProfileError("Name is required");
      return;
    }
    setProfileSaving(true);
    try {
      // NOTE: was ``/me/profile`` until 2026-07-14 - that route never
      // existed on the backend and every save 404ed. ``PUT /me`` is
      // the real profile-update endpoint.
      const { data } = await api.put("/me", { full_name: name });
      // TODO: Phase 5+ - merge full server response into the auth store
      // via a dedicated `useProfile` hook. For now mirror just `name`.
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        useAuthStore.setState({
          user: { ...currentUser, name: data.full_name ?? name },
        });
      }
      setProfileSuccess("Profile saved.");
    } catch (err: unknown) {
      const detail = (err as {
        response?: { data?: { detail?: string } };
      })?.response?.data?.detail;
      setProfileError(
        typeof detail === "string" ? detail : "Could not save profile.",
      );
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = async (e: FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);
    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match.");
      return;
    }
    if (newPassword.length < 12) {
      setPasswordError("New password must be at least 12 characters.");
      return;
    }
    setPasswordSaving(true);
    try {
      await api.put("/me/password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setPasswordSuccess("Password changed.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: unknown) {
      const detail = (err as {
        response?: { data?: { detail?: string } };
      })?.response?.data?.detail;
      setPasswordError(
        typeof detail === "string" ? detail : "Could not change password.",
      );
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card padding="lg">
        <h2 className="text-lg font-semibold text-neutral-900">
          Profile
        </h2>
        <p className="mt-1 text-sm text-neutral-500">
          Update your display name. Your email address and Buyist customer
          record are managed by an admin.
        </p>

        <form onSubmit={handleProfileSave} className="mt-6 space-y-4">
          {profileError && (
            <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
              {profileError}
            </div>
          )}
          {profileSuccess && (
            <div className="rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {profileSuccess}
            </div>
          )}

          <div>
            <label
              htmlFor="profile-name"
              className="block text-sm font-medium text-neutral-700"
            >
              Full name
            </label>
            <input
              id="profile-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700">
              Email
            </label>
            <p className="mt-1 text-sm text-neutral-600">{user?.email}</p>
            {/* TODO: Phase 5+ wire the email-change OTP flow
                (RequestEmailChange + ConfirmEmailChange endpoints). */}
          </div>

          <Button type="submit" isLoading={profileSaving}>
            Save profile
          </Button>
        </form>
      </Card>

      <EmailPreferencesCard />

      <Card padding="lg">
        <h2 className="text-lg font-semibold text-neutral-900">
          Change password
        </h2>
        <p className="mt-1 text-sm text-neutral-500">
          Passwords are at least 12 characters with mixed case and a digit.
        </p>

        <form onSubmit={handlePasswordChange} className="mt-6 space-y-4">
          {passwordError && (
            <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
              {passwordError}
            </div>
          )}
          {passwordSuccess && (
            <div className="rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {passwordSuccess}
            </div>
          )}

          <div>
            <label
              htmlFor="current-password"
              className="block text-sm font-medium text-neutral-700"
            >
              Current password
            </label>
            <input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>

          <div>
            <label
              htmlFor="new-password-set"
              className="block text-sm font-medium text-neutral-700"
            >
              New password
            </label>
            <input
              id="new-password-set"
              type="password"
              autoComplete="new-password"
              minLength={12}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>

          <div>
            <label
              htmlFor="confirm-password-set"
              className="block text-sm font-medium text-neutral-700"
            >
              Confirm new password
            </label>
            <input
              id="confirm-password-set"
              type="password"
              autoComplete="new-password"
              minLength={12}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>

          <Button type="submit" isLoading={passwordSaving}>
            Change password
          </Button>
        </form>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Email preferences card (Include Ticket History, 2026-07-14)
// ---------------------------------------------------------------------------

/**
 * Include Ticket History toggle. Self-service for every role
 * (customers included - per Greg, "by them, not just Agents"). The
 * flag lives on the user row, not the preferences blob, because the
 * backend send path reads it: when the REQUESTER of a ticket has it
 * on, reply emails carry the trimmed prior conversation.
 */
function EmailPreferencesCard(): JSX.Element {
  const { data: profile, isLoading } = useProfile();
  const updateProfile = useUpdateProfile();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const enabled = profile?.email_include_history ?? false;

  const handleToggle = async (next: boolean) => {
    setError(null);
    setSuccess(null);
    try {
      await updateProfile.mutateAsync({ email_include_history: next });
      setSuccess(
        next
          ? "Ticket emails will now include the conversation history."
          : "Ticket emails will now contain only the latest reply.",
      );
    } catch {
      setError("Could not save the setting. Please try again.");
    }
  };

  return (
    <Card padding="lg">
      <h2 className="text-lg font-semibold text-neutral-900">
        Email preferences
      </h2>
      <p className="mt-1 text-sm text-neutral-500">
        Choose how much context ticket reply emails include.
      </p>

      <div className="mt-6 space-y-4">
        {error && (
          <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}
        {success && (
          <div className="rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {success}
          </div>
        )}

        <label className="flex items-start gap-3 text-sm text-neutral-700">
          <input
            type="checkbox"
            checked={enabled}
            disabled={isLoading || updateProfile.isPending}
            onChange={(e) => void handleToggle(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
          />
          <span>
            <span className="font-medium text-neutral-900">
              Include ticket history
            </span>
            <span className="mt-0.5 block text-neutral-500">
              Replies to tickets you opened include the earlier
              conversation below the newest message, so you can follow
              the whole thread from your inbox. Internal notes are never
              included.
            </span>
          </span>
        </label>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Security tab - Phase 4 stub
// ---------------------------------------------------------------------------

function SecurityTab(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const [searchParams] = useSearchParams();
  const adminMfaRequired =
    searchParams.get("admin_mfa") === "required" && !user?.mfaEnabled;

  return (
    <div className="space-y-6">
      {adminMfaRequired && (
        <div className="rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Your role requires two-factor authentication. Please set up TOTP or
          a passkey below to keep access to your account.
        </div>
      )}

      <Card padding="lg">
        <h2 className="text-lg font-semibold text-neutral-900">
          Two-factor authentication (TOTP)
        </h2>
        <p className="mt-1 text-sm text-neutral-500">
          {user?.mfaEnabled
            ? "TOTP is enabled on your account."
            : "Use an authenticator app (1Password, Authy, Google Authenticator) to require a 6-digit code at sign-in."}
        </p>
        <div className="mt-4">
          {/* TODO: Phase 5+ - wire `/auth/totp/setup`,
              `/auth/totp/verify-setup`, and disable / regenerate
              endpoints from this button. The full QR + verify wizard
              mirrors Auware's SecurityTab. */}
          <Button variant="outline" disabled>
            {user?.mfaEnabled ? "Manage TOTP" : "Set up TOTP"}
          </Button>
          <p className="mt-2 text-xs text-neutral-400">
            Setup wizard ships in a follow-up phase.
          </p>
        </div>
      </Card>

      <Card padding="lg">
        <h2 className="text-lg font-semibold text-neutral-900">Passkeys</h2>
        <p className="mt-1 text-sm text-neutral-500">
          {user?.hasPasskeys
            ? "You have one or more passkeys registered."
            : "Register a passkey (Face ID, Touch ID, or a hardware key) for password-free sign-in."}
        </p>
        <div className="mt-4">
          {/* TODO: Phase 5+ - list passkeys via GET /auth/passkeys, add
              via /auth/passkeys/register/*, delete via DELETE
              /auth/passkeys/{id}. The full management UI mirrors
              Auware's SecurityTab. */}
          <Button variant="outline" disabled>
            Manage passkeys
          </Button>
          <p className="mt-2 text-xs text-neutral-400">
            Passkey management UI ships in a follow-up phase.
          </p>
        </div>
      </Card>

      <Card padding="lg">
        <h2 className="text-lg font-semibold text-neutral-900">
          Active sessions
        </h2>
        <p className="mt-1 text-sm text-neutral-500">
          See where you're signed in and revoke any session you don't
          recognize.
        </p>
        <div className="mt-4">
          {/* TODO: Phase 5+ - list via GET /auth/sessions, revoke single
              via DELETE /auth/sessions/{id}, revoke all-but-current via
              DELETE /auth/sessions. */}
          <Button variant="outline" disabled>
            View sessions
          </Button>
          <p className="mt-2 text-xs text-neutral-400">
            Session list ships in a follow-up phase.
          </p>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Team tab - agents + system_admins. Two actions: Disable / Enable login
// (PUT /admin/users/{id} with is_active flip) and Create new (modal that
// POSTs to /admin/users). The full multi-role admin surface still lives
// at /admin/users; this is the focused agent-only slice Greg asked for
// on 2026-05-18 so staff onboarding does not require leaving the
// settings flow.
// ---------------------------------------------------------------------------

function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return "Never";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function TeamTab(): JSX.Element {
  const usersQuery = useAdminUsers({
    staff_only: true,
    include_deleted: false,
    page: 1,
    page_size: 100,
  });
  const updateUser = useUpdateAdminUser();
  const createUser = useCreateAdminUser();

  const [pendingToggle, setPendingToggle] = useState<AdminUserResponse | null>(
    null,
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function handleConfirmToggle() {
    if (!pendingToggle) return;
    setActionError(null);
    try {
      await updateUser.mutateAsync({
        id: pendingToggle.id,
        body: { is_active: !pendingToggle.is_active },
      });
      setPendingToggle(null);
    } catch (e) {
      setActionError(extractDetailMessage(e, "Failed to update the user."));
    }
  }

  const rows = usersQuery.data?.items ?? [];

  return (
    <div className="space-y-6">
      <Card padding="lg">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">Team</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Agents and system administrators with access to the
              support inbox. Disabling login keeps the audit trail but
              blocks future sign-ins.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => {
              setActionError(null);
              setCreateOpen(true);
            }}
            className="shrink-0 whitespace-nowrap"
          >
            <i className="fa-duotone fa-thin fa-plus" aria-hidden="true" />
            Create new
          </Button>
        </div>

        {actionError && (
          <Alert variant="error" className="mt-4">
            {actionError}
          </Alert>
        )}

        <div className="mt-6">
          {usersQuery.isError && (
            <Alert variant="error" title="Could not load team">
              Try refreshing the page.
            </Alert>
          )}

          {usersQuery.isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                      Name
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                      Email
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                      Role
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                      Created
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {rows.length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-8 text-center text-sm text-neutral-500"
                      >
                        No agents yet.
                      </td>
                    </tr>
                  )}
                  {rows.map((u) => (
                    <tr key={u.id} className="hover:bg-neutral-50">
                      <td className="px-4 py-3 text-sm font-medium text-neutral-900">
                        {u.full_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700">
                        {u.email}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {u.role === "system_admin" ? (
                          <Badge variant="info">System admin</Badge>
                        ) : (
                          <Badge variant="default">Agent</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {u.is_active ? (
                          <Badge variant="success">Active</Badge>
                        ) : (
                          <Badge variant="warning">Disabled</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-500">
                        {formatTimestamp(u.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setActionError(null);
                            setPendingToggle(u);
                          }}
                          disabled={updateUser.isPending}
                        >
                          {u.is_active ? "Disable login" : "Enable login"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      <TeamConfirmToggleModal
        target={pendingToggle}
        isPending={updateUser.isPending}
        onCancel={() => setPendingToggle(null)}
        onConfirm={() => void handleConfirmToggle()}
      />

      <TeamCreateModal
        isOpen={createOpen}
        isPending={createUser.isPending}
        onCancel={() => setCreateOpen(false)}
        onSubmit={async (body) => {
          setActionError(null);
          try {
            await createUser.mutateAsync(body);
            setCreateOpen(false);
          } catch (e) {
            setActionError(extractDetailMessage(e, "Failed to create user."));
          }
        }}
      />
    </div>
  );
}

function TeamConfirmToggleModal({
  target,
  isPending,
  onCancel,
  onConfirm,
}: {
  target: AdminUserResponse | null;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  const willDisable = target?.is_active ?? false;
  return (
    <Modal
      isOpen={target !== null}
      onClose={onCancel}
      title={willDisable ? "Disable login?" : "Re-enable login?"}
    >
      <p className="text-sm text-neutral-700">
        {willDisable ? (
          <>
            <span className="font-medium">{target?.full_name}</span> (
            <span className="font-mono">{target?.email}</span>) will no
            longer be able to sign in. Their ticket history is preserved.
          </>
        ) : (
          <>
            <span className="font-medium">{target?.full_name}</span> (
            <span className="font-mono">{target?.email}</span>) will be
            able to sign in again.
          </>
        )}
      </p>
      <div className="mt-4 flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          size="sm"
          className={
            willDisable
              ? "bg-red-600 hover:bg-red-700 active:bg-red-800"
              : undefined
          }
          onClick={onConfirm}
          isLoading={isPending}
        >
          {willDisable ? "Disable login" : "Enable login"}
        </Button>
      </div>
    </Modal>
  );
}

function TeamCreateModal({
  isOpen,
  isPending,
  onCancel,
  onSubmit,
}: {
  isOpen: boolean;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (body: {
    email: string;
    full_name: string;
    role: SystemRole;
    send_setup_email: boolean;
  }) => Promise<void>;
}): JSX.Element {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<SystemRole>("agent");
  const [sendSetup, setSendSetup] = useState(true);

  useEffect(() => {
    if (!isOpen) {
      setEmail("");
      setFullName("");
      setRole("agent");
      setSendSetup(true);
    }
  }, [isOpen]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email.trim() || !fullName.trim()) return;
    await onSubmit({
      email: email.trim(),
      full_name: fullName.trim(),
      role,
      send_setup_email: sendSetup,
    });
  }

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title="Create team member">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          id="team-create-email"
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="agent@buyist.com"
        />
        <Input
          id="team-create-name"
          label="Full name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          required
          placeholder="Jane Doe"
        />
        <Select
          id="team-create-role"
          label="Role"
          value={role}
          onChange={(e) => setRole(e.target.value as SystemRole)}
          options={[
            { value: "agent", label: "Agent" },
            { value: "system_admin", label: "System admin" },
          ]}
        />
        <label className="flex items-center gap-2 text-sm text-neutral-700">
          <input
            type="checkbox"
            checked={sendSetup}
            onChange={(e) => setSendSetup(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
          />
          Send password setup email
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            isLoading={isPending}
          >
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Developers tab - SPEC §5 + Phase 9.10
// ---------------------------------------------------------------------------

function extractErrorMessage(err: unknown, fallback: string): string {
  const detail = (err as {
    response?: { data?: { detail?: string } };
  })?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
}

function DevelopersTab(): JSX.Element {
  const keysQuery = useApiKeys();
  const createKey = useCreateApiKey();
  const revokeKey = useRevokeApiKey();

  const [createOpen, setCreateOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [revealedKey, setRevealedKey] = useState<{
    name: string;
    rawKey: string;
  } | null>(null);

  const handleCreate = async (body: ApiKeyCreateRequest) => {
    setCreateError(null);
    try {
      const created = await createKey.mutateAsync(body);
      setRevealedKey({ name: created.name, rawKey: created.raw_key });
      setCreateOpen(false);
    } catch (err) {
      setCreateError(extractErrorMessage(err, "Could not create API key."));
      throw err;
    }
  };

  return (
    <div className="space-y-6">
      <Card padding="lg">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">
              API keys
            </h2>
            <p className="mt-1 text-sm text-neutral-500">
              Issue `bsk_live_` keys for the public API. Each key is hashed
              at rest; the cleartext value is shown exactly once at
              creation.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="shrink-0 whitespace-nowrap"
          >
            <i
              className="fa-duotone fa-thin fa-plus"
              aria-hidden="true"
            />
            Create new key
          </Button>
        </div>

        <div className="mt-6">
          {keysQuery.isError && (
            <Alert variant="error" title="Could not load API keys">
              Try refreshing the page. If the problem persists, check the
              backend logs.
            </Alert>
          )}

          {keysQuery.isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <ApiKeyTable
              keys={keysQuery.data ?? []}
              onRevoke={(id) => revokeKey.mutate(id)}
              isRevoking={revokeKey.isPending}
            />
          )}
        </div>
      </Card>

      <CreateApiKeyModal
        isOpen={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setCreateError(null);
        }}
        onSubmit={handleCreate}
        isSubmitting={createKey.isPending}
        error={createError}
      />

      {revealedKey && (
        <RevealOnceModal
          isOpen
          onClose={() => setRevealedKey(null)}
          title={`API key created: ${revealedKey.name}`}
          description="Use this value as the bearer token. Send it via `Authorization: Bearer <key>` on every public API request."
          secret={revealedKey.rawKey}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SettingsPage
// ---------------------------------------------------------------------------

export function SettingsPage(): JSX.Element {
  usePageTitle("Settings");
  const user = useAuthStore((s) => s.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab") as TabKey | null;
  const [activeTab, setActiveTab] = useState<TabKey>(
    requestedTab ?? "profile",
  );

  // Customers never see the Team or Developers tabs.
  const tabs = user?.isSystemAdmin
    ? BASE_TABS
    : BASE_TABS.filter((t) => t.key !== "team" && t.key !== "developers");

  useEffect(() => {
    if (requestedTab && tabs.some((t) => t.key === requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [requestedTab, tabs]);

  const handleTabClick = (key: TabKey) => {
    setActiveTab(key);
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-neutral-900">Settings</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Manage your profile, security, and team access.
        </p>
      </div>

      {/* Horizontal underline pills */}
      <div className="border-b border-neutral-200">
        <nav className="-mb-px flex gap-6">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleTabClick(tab.key)}
                className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-neutral-500 hover:border-neutral-300 hover:text-neutral-700"
                }`}
              >
                <i className={`${tab.icon} text-lg`} aria-hidden="true" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === "profile" && <ProfileTab />}
      {activeTab === "security" && <SecurityTab />}
      {activeTab === "team" && user?.isSystemAdmin && <TeamTab />}
      {activeTab === "developers" && user?.isSystemAdmin && <DevelopersTab />}
    </div>
  );
}

export default SettingsPage;
